// Game-run recorder. Posts a finished run to POST /api/runs and swallows
// any failure — never let a stat write break gameplay. Anonymous users
// skip the call entirely (no auth, nothing to record).
//
// Lives in web rather than @atlasguess/games because games is intentionally
// UI/transport-free — wiring fetch + csrf into it would couple it to the
// SPA's auth model. Mode files import from here directly.

import { api, ApiError } from './api';
import { user } from './stores/auth';
import { get } from 'svelte/store';

export type ScoreUnit = 'km' | 'ms' | 'count';

export interface RoundPayload {
  /** City id, country iso2, etc — whatever the round asked the user about. */
  target_id: string;
  /** Numeric score; meaning depends on score_unit. */
  score: number;
  /** How to interpret `score`. Must match the unit the mode normally writes. */
  score_unit: ScoreUnit;
  /**
   * Optional player guess coordinates. Sent by the city-distance Practice
   * mode so the per-city history panel can render past-guess markers and
   * compute best-ever / worst-ever stats. Daily and non-distance modes
   * leave both null.
   */
  lat?: number;
  lon?: number;
  /**
   * First-try-correct flag — sent per-round by quiz mode so the stats
   * aggregator can compute "best run" by (correct DESC, ms ASC).
   * Other modes leave this undefined; legacy rows have NULL in the DB
   * and the aggregator treats them as no-data (excluded from best
   * computation).
   */
  first_try?: boolean;
}

export interface RecordRunInput {
  /** Registry mode id, e.g. 'cities-world-daily'. */
  mode_id: string;
  /** Format axis, denormalized so the server can index without a join. */
  format: 'daily' | 'practice' | 'quiz';
  /**
   * Idempotency key. Daily uses 'cities-world-daily-2026-04-27' (one per
   * day per mode); practice and quiz use a per-run identifier. Combined
   * with user_id this is the upsert key on the server.
   */
  run_key: string;
  rounds: RoundPayload[];
  /** ISO 8601 string. Defaults to "now" if omitted. */
  played_at?: string;

  /**
   * Region-practice streak fields. Sent by ClickInRegionPractice on
   * each per-round POST so the server can keep a per-(mode + filter)
   * best-streak running max. `filter_slug` is the URL filter segment
   * (e.g. 'africa') or empty string for unfiltered. `current_streak`
   * is the streak count AFTER this round's outcome — wrong rounds
   * send 0, correct rounds send the new total.
   */
  filter_slug?: string;
  current_streak?: number;
}

/** Shape returned by GET /api/account/daily-status. */
export interface DailyStatus {
  completed: boolean;
  rounds: Array<{ target_id: string; score: number; score_unit: ScoreUnit }>;
}

/**
 * Fetch today's daily-mode completion state for the authed user.
 * Returns null for anonymous users (caller falls back to localStorage)
 * or on any failure (so an outage doesn't strand the player on the
 * loading screen).
 *
 * The run_key encodes the player's local date — pass exactly what the
 * recorder writes so the server lookup matches the row.
 */
export async function fetchDailyStatus(
  modeId: string,
  runKey: string,
): Promise<DailyStatus | null> {
  if (!get(user)) return null;
  try {
    const params = new URLSearchParams({ mode_id: modeId, run_key: runKey });
    return await api<DailyStatus>(`/api/account/daily-status?${params}`);
  } catch (err) {
    if (err instanceof ApiError) {
      console.warn('[runs] daily-status fetch failed', err.status, err.message);
    } else {
      console.warn('[runs] daily-status fetch failed', err);
    }
    return null;
  }
}

/**
 * Fire-and-forget run recorder. Returns true on success, false if the
 * write was skipped (anonymous) or failed (network/server). Never throws —
 * a failed stat write must not interrupt the post-round UI.
 */
export async function recordRun(input: RecordRunInput): Promise<boolean> {
  // Anonymous players don't have an account to attach the row to.
  if (!get(user)) return false;

  try {
    await api('/api/runs', {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        played_at: input.played_at ?? new Date().toISOString(),
      }),
    });
    return true;
  } catch (err) {
    // Logged so debugging is possible, but never surfaced to the player.
    if (err instanceof ApiError) {
      console.warn('[runs] record failed', err.status, err.message);
    } else {
      console.warn('[runs] record failed', err);
    }
    return false;
  }
}
